class NewsletterParallax extends HTMLElement {
  constructor() {
    super();
    this.section = null;
    this.decoElements = [];
    this.animationFrame = null;
    this.scrollHandler = this.handleScroll.bind(this);
  }

  connectedCallback() {
    // Wait for next frame to ensure DOM is fully ready
    requestAnimationFrame(() => {
      // The section is a child of this custom element
      this.section = this.querySelector('.newsletter-section');
      if (!this.section) return;

      // Get all decorative elements
      this.decoElements = Array.from(this.section.querySelectorAll('.newsletter-deco-1, .newsletter-deco-2, .newsletter-deco-3'));
      
      if (this.decoElements.length === 0) return;

      // Check for reduced motion preference
      const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      if (prefersReducedMotion) return;

      // Initialize parallax
      this.initParallax();
    });
  }

  initParallax() {
    // Set initial positions
    this.updateParallax();
    
    // Always listen to scroll, but only update when section is visible
    window.addEventListener('scroll', this.scrollHandler, { passive: true });
    
    // Also listen to resize to recalculate
    window.addEventListener('resize', this.scrollHandler, { passive: true });
    
    // Use IntersectionObserver to optimize performance
    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          this.updateParallax();
        }
      });
    }, {
      rootMargin: '100px'
    });

    observer.observe(this.section);
  }

  handleScroll() {
    if (this.animationFrame) {
      cancelAnimationFrame(this.animationFrame);
    }
    
    this.animationFrame = requestAnimationFrame(() => {
      this.updateParallax();
    });
  }

  updateParallax() {
    if (!this.section || this.decoElements.length === 0) return;

    const rect = this.section.getBoundingClientRect();
    const sectionTop = rect.top;
    const sectionHeight = rect.height;
    const viewportHeight = window.innerHeight;
    
    // Calculate how much of the section is visible and its position
    // When section is at top of viewport: progress = 0
    // When section is at bottom of viewport: progress = 1
    const scrollProgress = Math.max(0, Math.min(1, (viewportHeight - sectionTop) / (viewportHeight + sectionHeight)));
    
    // Element configurations: [speed, delayOffset]
    // delayOffset creates a lag effect (0 = no delay, higher = more lag)
    const elementConfigs = [
      [-80, 0.15], // deco-1: fast upward, 15% delay
      [70, 0],     // deco-2: slower downward, no delay
      [-75, 0]     // deco-3: fast upward, no delay
    ];
    
    // Apply different parallax speeds and delays to each element
    this.decoElements.forEach((element, index) => {
      const [speed, delay] = elementConfigs[index] || [0, 0];
      
      // Apply delay offset to scroll progress for staggered effect
      const delayedProgress = Math.max(0, Math.min(1, scrollProgress - delay));
      
      // Calculate transform based on delayed scroll progress
      // Map progress from 0-1 to -speed to +speed for more dynamic effect
      const translateY = (delayedProgress - 0.5) * speed * 2;
      
      // Apply transform
      element.style.transform = `translateY(${translateY}px)`;
    });
  }

  disconnectedCallback() {
    window.removeEventListener('scroll', this.scrollHandler);
    window.removeEventListener('resize', this.scrollHandler);
    if (this.animationFrame) {
      cancelAnimationFrame(this.animationFrame);
    }
  }
}

customElements.define('newsletter-parallax', NewsletterParallax);

